import Select from "@/components/Select";
import React from "react";
import { SerializedCondition } from "../types";
import useTeams from "@/hooks/useTeams";

export type TeamQueueSizeCondition = {
  teamId: number;
  threshold: number;
  comparator: "greaterThan" | "greaterThanOrEqual" | "lessThan" | "equals";
};

const COMPARATOR_LABELS: Record<string, string> = {
  greaterThan: "more than",
  greaterThanOrEqual: "at least",
  lessThan: "fewer than",
  equals: "exactly"
};

export function TeamQueueSizeEditor({
  condition,
  onUpdate
}: {
  condition: SerializedCondition;
  onUpdate: (condition: SerializedCondition) => void;
}) {
  const { teams } = useTeams();
  const data = JSON.parse(condition.data || "{}") as Partial<TeamQueueSizeCondition>;

  const teamOptions = (Array.isArray(teams) ? teams : []).reduce(
    (acc, t) => { acc[t.id.toString()] = t.name; return acc; },
    {} as Record<string, string>
  );

  function update(patch: Partial<TeamQueueSizeCondition>) {
    onUpdate({ ...condition, data: JSON.stringify({ ...data, ...patch }) });
  }

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <Select
        options={{ "": "— pick team —", ...teamOptions }}
        value={data.teamId?.toString() ?? ""}
        onChange={(v) => update({ teamId: Number(v) })}
      />
      <span className="text-xs text-slate-500 whitespace-nowrap">queue has</span>
      <Select
        options={COMPARATOR_LABELS}
        value={data.comparator ?? "greaterThan"}
        onChange={(v) => update({ comparator: v as TeamQueueSizeCondition["comparator"] })}
      />
      <input
        type="number"
        min={0}
        max={9999}
        value={data.threshold ?? 5}
        onChange={(e) => update({ threshold: Number(e.target.value) })}
        className="w-16 border border-slate-200 rounded-md px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-cornflower-blue-300"
        aria-label="Queue depth threshold"
      />
      <span className="text-xs text-slate-500">conversations waiting</span>
    </div>
  );
}
